/**
 * Quick 260914-huu — pure overlay-ordering helper for the Kanban milestone
 * swimlane rows. Co-located with `milestoneCounts.ts` / `milestoneStatus.ts`
 * (same directory, same "pure helper extracted from the component,
 * unit-tested on its own" convention).
 *
 * CONTRACT — the stored `order` array is a permutation OVERLAY over the
 * input list, never a replacement:
 *   1. milestones whose id appears in `order` come first, in `order`'s order
 *   2. milestones absent from `order` follow, in their input relative order
 *   3. ids in `order` that match no current milestone are ignored (stale —
 *      pruned implicitly, no hole, no crash)
 *   4. a duplicate id in `order` takes its first position only
 *   5. `order` undefined or empty → input list returned unchanged (server
 *      default: `startDate DESC NULLS LAST, createdAt DESC`)
 *
 * The input array is never mutated; a new array is always returned.
 */
export function applyMilestoneOrder<T extends { id: number }>(
  milestones: T[],
  order: number[] | undefined,
): T[] {
  if (!order || order.length === 0) {
    return [...milestones];
  }

  const byId = new Map<number, T>(milestones.map((m) => [m.id, m]));
  const placed = new Set<number>();
  const result: T[] = [];

  for (const id of order) {
    if (placed.has(id)) continue; // duplicate id — first position only
    const milestone = byId.get(id);
    if (!milestone) continue; // stale id — no matching milestone, ignore
    result.push(milestone);
    placed.add(id);
  }

  for (const m of milestones) {
    if (!placed.has(m.id)) {
      result.push(m);
    }
  }

  return result;
}
